import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getSurveys } from '../../../api/surveyQuestion.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import { IGetSurveys, ISurveyItem } from '../../../models/surveyQuestion.model';
import { RootState } from '../../../store/rootReducer';
import useUtil from '../../../hooks/useUtil';

const Survey = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // HP0007 = 설문조사 유형
  const HP0008 = commonCodes.find(
    (item) => item.commonCode === 'HP0008',
  )?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetSurveys>();

  const [surveyType, setSurveyType] = useState<string>();
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [params, setParams] = useState({});
  const [campusDeactived, setCampusDeactived] = useState(false);

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    if (currentCampus) {
      const _params: any = {
        pageNo: page,
        perPage: config.pageSize,
        campusCode: currentCampus,
      };

      if (query.page) {
        _params.pageNo = Number(query.page);
        setPage(_params.pageNo);
      }
      if (query.title) {
        _params.title = String(query.title);
        setTitle(_params.title);
      }
      if (query.surveyType) {
        _params.surveyType = String(query.surveyType);
        setSurveyType(_params.surveyType);
      }

      execute(getSurveys(_params));
      _params.page = _params.pageNo;
      delete _params.pageNo;
      delete _params.perPage;
      setParams(_params);
    }
  }, [currentCampus, location]);

  const onSearch = () => {
    const params: any = { page: 1 };

    if (surveyType) params.surveyType = surveyType;
    if (title) params.title = title;

    history.push(`/homepage/survey?${QueryString.stringify(params)}`);
  };

  const columns: TableColumnsType<ISurveyItem> = [
    {
      title: t('homepage.archive.column1'),
      dataIndex: 'surveyCode',
      key: 'surveyCode',
      align: 'center',
      width: '6%',
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('homepage.survey.column1'),
      dataIndex: 'surveyType',
      key: 'surveyType',
      align: 'center',
      width: '10%',
      render: (value) => getCommonCodeLabel('HP0008', value, 'ko'),
    },
    {
      title: t('homepage.survey.column2'),
      dataIndex: 'srtDate',
      key: 'srtDate',
      align: 'center',
      width: '10%',
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('homepage.survey.column3'),
      dataIndex: 'endDate',
      key: 'endDate',
      align: 'center',
      width: '10%',
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('common.column.writer'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
      width: '10%',
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('common.menu.homepage')}
      currentMenu={t('homepage.survey.title1')}
      onlyTitle={true}
    />	
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('homepage.survey.column1')}</label>
            <div>
              <Select
                placeholder={t('homepage.survey.column1')}
                value={surveyType}
                onChange={(value) => setSurveyType(value)}
              >
                <Select.Option value="">
                  {t('common.text.all')}
                </Select.Option>
                {HP0008?.map((item, index) => (
                  <Select.Option key={index} value={item.detailCode}>
                    {i18n.language === 'ko' ? item.korName : item.engName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('common.column.title')}</label>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('homepage.inquiry.placeholder1')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{res?.total}</b>
            {t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/survey/create')}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/homepage/survey/${record.surveyCode}/edit${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="noticeCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={params}
              path="/homepage/survey"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/survey/create')}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Survey;
